import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { JobTitleService } from "services/hiring/jobTitle.service";
import { SalaryScaleService } from "services/hiring/salaryScale.service";
import { Observable, of, Subject } from "rxjs";
import { catchError, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { guidEmpty } from "utils/helps";
import { MovementSalaryRange } from "../../form-types";

type SalaryRangeLoadState = "idle" | "loading" | "loaded" | "failed";

/**
 * Campo "Faixa Salarial" das movimentações do Orgânico.
 * Só aparece quando a função informada possui grade salarial com faixas ativas e valor preenchido.
 * Enquanto as faixas carregam (ou se a consulta falhar) o controle fica inválido, para não enviar a solicitação sem faixa.
 */
@Component({
	selector: "app-salary-range-select",
	templateUrl: "./salary-range-select.component.html",
})
export class SalaryRangeSelectComponent implements OnInit, OnChanges, OnDestroy {
	@Input() jobTitleId: string | null = null;
	@Input() control!: AbstractControl;
	/** Quando falso, a faixa é opcional (ex.: homologação de solicitação criada antes da grade existir). */
	@Input() required = true;
	@Output() rangeSelected = new EventEmitter<MovementSalaryRange | null>();

	options: MovementSalaryRange[] = [];
	state: SalaryRangeLoadState = "idle";

	private readonly _jobTitleId$ = new Subject<string | null>();
	private readonly _destroy$ = new Subject<void>();
	private readonly _validator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		if (!this.required) return null;
		if (this.state === "loading") return { salaryRangeLoading: true };
		if (this.state === "failed") return { salaryRangeLoadFailed: true };
		if (this.options.length && !control.value) return { required: true };
		return null;
	};

	constructor(
		private _jobTitleService: JobTitleService,
		private _salaryScaleService: SalaryScaleService
	) {}

	ngOnInit(): void {
		this.control?.setValidators(this._validator);

		this._jobTitleId$
			.pipe(
				tap((jobTitleId) => this._startLoading(jobTitleId)),
				switchMap((jobTitleId) => this._loadRanges(jobTitleId)),
				takeUntil(this._destroy$)
			)
			.subscribe((ranges) => this._applyRanges(ranges));

		this._jobTitleId$.next(this.jobTitleId);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["required"] && !changes["required"].firstChange) {
			this.control?.updateValueAndValidity();
		}

		const change = changes["jobTitleId"];
		if (!change || change.firstChange) return;

		if (change.previousValue && change.previousValue !== change.currentValue && this.control?.enabled) {
			const hadSelectedRange = !!this.control.value;
			this.control.setValue(null);
			// O pai sincroniza o salário por rangeSelected. Sem o emit, newSalary fica com o valor da faixa anterior.
			if (hadSelectedRange) {
				this.rangeSelected.emit(null);
			}
		}
		this._jobTitleId$.next(change.currentValue);
	}

	ngOnDestroy(): void {
		this.control?.clearValidators();
		this.control?.updateValueAndValidity();
		this._destroy$.next();
		this._destroy$.complete();
	}

	get isVisible(): boolean {
		if (!this.control?.enabled) return this.options.length > 0 && !!this.control?.value;
		return this.state === "loading" || this.state === "failed" || this.options.length > 0;
	}

	get showRequiredError(): boolean {
		return !!this.control?.touched && !!this.control?.hasError("required");
	}

	formatLabel(range: MovementSalaryRange): string {
		const amount = (range.amount ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
		return `${range.code} - ${amount}`;
	}

	compareRanges(a: MovementSalaryRange, b: MovementSalaryRange): boolean {
		return a?.salaryRangeId === b?.salaryRangeId;
	}

	onChange(range: MovementSalaryRange | null): void {
		this.rangeSelected.emit(range ?? null);
	}

	retry(): void {
		this._jobTitleId$.next(this.jobTitleId);
	}

	private _startLoading(jobTitleId: string | null): void {
		this.options = [];
		this.state = jobTitleId ? "loading" : "idle";
		this.control?.updateValueAndValidity();
	}

	private _loadRanges(jobTitleId: string | null): Observable<MovementSalaryRange[] | null> {
		if (!jobTitleId) return of([]);

		return this._jobTitleService
			.getListFiltered(["$select=id,salaryScaleId", `$filter=id eq ${jobTitleId}`])
			.pipe(
				switchMap(([jobTitle]) => {
					const salaryScaleId = jobTitle?.salaryScaleId;
					if (!salaryScaleId || salaryScaleId === guidEmpty) return of([]);

					return this._salaryScaleService.getSalaryRanges(salaryScaleId).pipe(
						map((res) =>
							(res.data ?? [])
								.filter((r) => r.enable && !!r.amount)
								.map((r) => ({
									salaryScaleId,
									salaryRangeId: r.id!,
									code: r.foreingId,
									amount: r.amount,
								}))
						)
					);
				}),
				// null = falha na consulta; não pode ser confundido com "função sem grade"
				catchError(() => of(null))
			);
	}

	private _applyRanges(ranges: MovementSalaryRange[] | null): void {
		if (!this.control) return;

		if (ranges === null) {
			this.options = [];
			this.state = "failed";
			this.control.updateValueAndValidity();
			return;
		}

		this.state = "loaded";
		this.options = ranges;

		const current = this.control.value as MovementSalaryRange | null;
		const isCurrentAvailable = !!current && ranges.some((r) => r.salaryRangeId === current.salaryRangeId);

		if (current && !isCurrentAvailable) {
			if (this.control.enabled) {
				this.control.setValue(null);
			} else {
				this.options = [current, ...ranges];
			}
		}

		this.control.updateValueAndValidity();
	}
}
